/**
 * Marks every instance of a substring that is contained in a (larger) string by using the HTML Mark
 * Text element. Comparison is case-insensitive.
 *
 * @example
 *   markSubstring('abBa', 'b'); // [ 'a', HTMLElement{<mark>b</mark>}, HTMLElement{<mark>B</mark>}, 'a' ]
 * @param string - String to match against.
 * @param substring - String to find inside of the first param.
 * @return parts - Array consisting of parts of the main string that don't match the
 *   substring and zero, one or more HTML <mark> elements that contain the matching substring.
 */
export function markSubstring (string: string, substring: string) {
  if (!substring) {
    return [ string ];
  }
  substring = substring.toLowerCase();
  const parts = [];
  while (string.length > 0) {
    const index = string.toLowerCase().indexOf(substring);
    if (index === -1) {
      parts.push(string);
      break;
    }
    if (index > 0) {
      parts.push(string.slice(0, index));
    }
    const marked = document.createElement('mark');
    marked.textContent = string.substr(index, substring.length);
    parts.push(marked);
    string = string.slice(index + substring.length);
  }
  return parts;
}

/**
 * Mark every instance of every specified string tokens in the content of the given HTML element
 * by using the HTML Mark element.
 *
 * Comparison is case-insensitive.
 */
export function markTokensInElement (el: Element | null, tokens?: string[]) {
  if (!el || !tokens?.length) {
    return el;
  }
  let parts: (Element | string)[] = [ el.textContent ?? '' ];
  for (const token of tokens) {
    const newParts = [];
    for (const part of parts) {
      if (typeof part === 'string') {
        const marked = markSubstring(part, token);
        newParts.push(...marked);
      } else {
        newParts.push(part);
      }
    }
    parts = newParts;
  }
  // eslint-disable-next-line no-unsanitized/property
  el.innerHTML = parts.map(part => {
    if (part instanceof HTMLElement) {
      return part.outerHTML;
    }
    return part;
  }).join('');
  return el;
}
